import { getApps, initializeApp } from "firebase/app";
import {
  type Auth,
  type AuthError,
  GoogleAuthProvider,
  getAuth,
  linkWithCredential,
  OAuthCredential,
  OAuthProvider,
  getRedirectResult,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { type Bootstrap, signinEvent } from "./api";
import { preloadGis, promptGoogle } from "./gis";
import {
  codeOf,
  displayMode,
  gisNext,
  logLine,
  type Method,
  methodFor,
  sameOrigin,
  type SigninEvent,
  type Step,
  usesGis,
} from "./signinFlow";

const EMAIL_KEY = "signinEmail";
const ORG_EMAIL_KEY = "signinEmailByOrg";
const PENDING_KEY = "pendingProvider";
const PENDING_MS = 15 * 60_000;
const REDIRECT_KEY = "providerRedirect";
const REDIRECT_MS = 10 * 60_000;

export type Provider = "google" | "microsoft";

export function authFor(slug: string, org: Bootstrap): Auth {
  const app =
    getApps().find((a) => a.name === slug) ??
    initializeApp({ apiKey: org.identity.api_key, authDomain: org.identity.auth_domain }, slug);
  const auth = getAuth(app);
  auth.tenantId = org.identity.tenant_id;
  return auth;
}

function currentDisplay() {
  const matches = (query: string) => window.matchMedia?.(query).matches ?? false;
  return displayMode(matches, (navigator as { standalone?: boolean }).standalone === true);
}

function report(auth: Auth, slug: string, name: Provider, method: Method, step: Step, error?: unknown) {
  const authHost = auth.config.authDomain ?? "";
  const event: SigninEvent = {
    slug,
    provider: name,
    method,
    step,
    display: currentDisplay(),
    auth_host: authHost,
    same_origin: sameOrigin(authHost, location.host),
    tenant: auth.tenantId ?? "",
    ...(error === undefined ? {} : { code: codeOf(error) }),
  };
  const line = logLine(event);
  if (step === "start" || step === "ok") console.info(line);
  else console.warn(line);
  signinEvent(event).catch(() => console.warn("sign-in log: the server did not take the event"));
}

async function redirectTo(auth: Auth, slug: string, name: Provider, provider: GoogleAuthProvider | OAuthProvider) {
  try {
    localStorage.setItem(REDIRECT_KEY, JSON.stringify({ slug, name, until: Date.now() + REDIRECT_MS }));
  } catch {
    console.warn("sign-in: no storage, so an error after the redirect will not show");
  }
  report(auth, slug, name, "redirect", "start");
  await signInWithRedirect(auth, provider);
}

/** Sign in with a Google ID token from the page; false when the popup or the redirect must run instead. */
async function signInWithGis(auth: Auth, slug: string, clientId: string, hint: string | null): Promise<boolean> {
  report(auth, slug, "google", "gis", "start");
  let result: Awaited<ReturnType<typeof promptGoogle>>;
  try {
    result = await promptGoogle(clientId, hint);
  } catch (error) {
    report(auth, slug, "google", "gis", "fallback", error);
    return false;
  }
  if (!("token" in result)) {
    const code = { code: `gis/${result.kind}/${result.reason}` };
    const next = gisNext(result);
    report(auth, slug, "google", "gis", next === "cancel" ? "cancel" : "fallback", code);
    return next === "cancel";
  }
  try {
    await signInWithCredential(auth, GoogleAuthProvider.credential(result.token));
  } catch (error) {
    report(auth, slug, "google", "gis", "error", error);
    throw error;
  }
  report(auth, slug, "google", "gis", "ok");
  return true;
}

export const prepareProvider = (name: Provider, googleClientId?: string) => {
  if (usesGis(name, googleClientId)) void preloadGis();
};

export async function signInWithProvider(
  auth: Auth,
  slug: string,
  name: Provider,
  hint?: string | null,
  googleClientId?: string,
) {
  if (usesGis(name, googleClientId) && (await signInWithGis(auth, slug, googleClientId!, hint ?? null))) return;
  const provider = name === "google" ? new GoogleAuthProvider() : new OAuthProvider("microsoft.com");
  provider.setCustomParameters({ prompt: "select_account", ...(hint ? { login_hint: hint } : {}) });
  if (methodFor(currentDisplay()) === "redirect") return redirectTo(auth, slug, name, provider);
  report(auth, slug, name, "popup", "start");
  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    if (codeOf(error) !== "auth/popup-blocked") {
      report(auth, slug, name, "popup", "error", error);
      throw error;
    }
    report(auth, slug, name, "popup", "fallback", error);
    return redirectTo(auth, slug, name, provider);
  }
  report(auth, slug, name, "popup", "ok");
}

export type RedirectOutcome = { name: Provider; error?: unknown; lost?: boolean };

const finishing = new Map<string, Promise<RedirectOutcome | null>>();

/** Give the outcome of a provider redirect of this organization once per page load, or null when none waits. */
export function finishRedirect(auth: Auth, slug: string): Promise<RedirectOutcome | null> {
  const known = finishing.get(slug);
  if (known) return known;
  const outcome = takeRedirect(auth, slug);
  finishing.set(slug, outcome);
  return outcome;
}

async function takeRedirect(auth: Auth, slug: string): Promise<RedirectOutcome | null> {
  let marker: { slug: string; name: Provider; until: number } | null = null;
  try {
    marker = JSON.parse(localStorage.getItem(REDIRECT_KEY) ?? "null");
    if (marker?.slug === slug) localStorage.removeItem(REDIRECT_KEY);
  } catch {
    return null;
  }
  if (!marker || marker.slug !== slug || marker.until < Date.now()) return null;
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      report(auth, slug, marker.name, "redirect", "ok");
      return { name: marker.name };
    }
    report(auth, slug, marker.name, "redirect", "lost");
    return { name: marker.name, lost: true };
  } catch (error) {
    report(auth, slug, marker.name, "redirect", "error", error);
    return { name: marker.name, error };
  }
}

export function keepPending(slug: string, name: Provider, error: AuthError): string | null {
  const credential =
    name === "google" ? GoogleAuthProvider.credentialFromError(error) : OAuthProvider.credentialFromError(error);
  const email = error.customData.email;
  if (!credential || !email) return null;
  try {
    const pending = { slug, name, email, credential: credential.toJSON(), until: Date.now() + PENDING_MS };
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    return null;
  }
  return email;
}

export function dropPending(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing to clean.
  }
}

export async function linkPending(auth: Auth, slug: string): Promise<Provider | null> {
  let pending: { slug: string; name: Provider; email: string; credential: object; until: number } | null = null;
  try {
    pending = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "null");
    localStorage.removeItem(PENDING_KEY);
  } catch {
    return null;
  }
  const user = auth.currentUser;
  if (!pending || !user || pending.slug !== slug || pending.until < Date.now()) return null;
  if (user.email?.toLowerCase() !== pending.email.toLowerCase()) return null;
  const credential = OAuthCredential.fromJSON(pending.credential);
  if (!credential) return null;
  try {
    await linkWithCredential(user, credential);
  } catch (error) {
    throw Object.assign(new Error(`link failed: ${String(error)}`), { provider: pending.name });
  }
  return pending.name;
}

function orgEmails(): Record<string, string> {
  const raw: unknown = JSON.parse(localStorage.getItem(ORG_EMAIL_KEY) ?? "{}");
  return raw && typeof raw === "object" ? (raw as Record<string, string>) : {};
}

/** Keep the email for the start page and, with a slug, as the sign-in hint of that organization only. */
export function rememberEmail(email: string, slug?: string): void {
  try {
    localStorage.setItem(EMAIL_KEY, email);
    if (slug) localStorage.setItem(ORG_EMAIL_KEY, JSON.stringify({ ...orgEmails(), [slug]: email }));
  } catch {
    // Without storage, the sign-in page asks for the email address again.
  }
}

export function savedEmail(slug?: string): string | null {
  try {
    const kept = slug ? orgEmails()[slug] : undefined;
    if (slug) return typeof kept === "string" ? kept : null;
    return localStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

export const signInWithToken = (auth: Auth, token: string) => signInWithCustomToken(auth, token);
